import { Injectable } from '@angular/core';
import {
  HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from "../../environments/environment";
import { AuthService } from '../services/authentication/auth.service';
import { ClientToastController } from '../toasts/client-toast.lib';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
    private toast: ClientToastController
  ) {}

  private async extractMessage(err: HttpErrorResponse, fallback: string): Promise<string> {
    const e: any = err.error;

    // Download endpoints can yield Blob errors because responseType:'blob'
    if (e instanceof Blob) {
      try {
        const text = await e.text();
        return (text && text.trim().length > 0) ? text : fallback;
      } catch {
        return fallback;
      }
    }

    if (typeof e === 'string') {
      return (e && e.trim().length > 0) ? e : fallback;
    }

    // If some endpoints return structured JSON, safely coerce to a readable string.
    const candidate = e?.message ?? e?.error;

    if (typeof candidate === 'string') {
      return candidate.trim().length > 0 ? candidate : fallback;
    }

    if (Array.isArray(candidate)) {
      const joined = candidate.filter((x) => typeof x === 'string').join(', ').trim();
      return joined.length > 0 ? joined : fallback;
    }

    if (candidate && typeof candidate === 'object') {
      const nestedMessage = (candidate as any).message;
      if (typeof nestedMessage === 'string' && nestedMessage.trim().length > 0) {
        return nestedMessage;
      }
    }

    return fallback;
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (!environment.production) {
          console.error(err);
        }

        const fallback = err.statusText || 'Request failed';

        return from(this.extractMessage(err, fallback)).pipe(
          mergeMap((serverMsg) => {
            if (err.status === 401) {
              this.authService.clearLocalStorage();
              if (!String(err.url ?? '').includes('refresh-token')) {
                this.authService.clearCurrentUser();
                this.router.navigate(['login']);
              }
              return throwError(() => err);
            }

            // Suppress toasts for aborted requests / client-side network errors
            if (err.status === 0) return throwError(() => err);
            
            let msg = typeof serverMsg === 'string' ? serverMsg : '';

            if (!msg.trim()) {
              if (err.status === 400) msg = 'Bad Request';
              else if (err.status === 403) msg = 'You are not authorized to perform this action.';
              else if (err.status === 404) msg = 'The requested resource was not found.';
              else msg = fallback;
            }

            this.toast.displayToast(`Error: ${msg}`, 5000);
            return throwError(() => err);
          })
        );
      })
    );
  }
}
